import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';

import { IconCloseSvg } from '../../../assets/inventory';
import {} from '../Achievements/achievementsTheme';
import { RedesignScrollView } from '../common/RedesignScrollView';
import { Pressable } from '../common/Touchable';
import RedesignModal from '../Modals/RedesignModal';
import { useRedesignScale } from '../Modals/useRedesignScale';
import {
	PANEL_FILL,
	PANEL_RIM,
	TOURNAMENT_FONT as FONT,
	tournamentColors as colors,
} from './tournamentModalTheme';

/**
 * Figma "Modal / Tournament Rules" (5157:14681) on page "PAVEL DRAFT", section "Tournaments
 * Elements": a 358x545 sheet over the tournament info modal, with the sit & go rules in five
 * sections. The rules are static copy, as they were in the old `TournamentRulesModal`; each block
 * reads a translation key and falls back to the design's English.
 */
const PANEL = { height: 545, radius: 10, paddingX: 12, paddingY: 20, gap: 16 };

type Section = { key: string; titleKey: string; title: string; body: string[]; accent?: boolean };

const SECTIONS: Section[] = [
	{
		key: 'REGISTRATION',
		titleKey: 'T_SITE_TOURNAMENT_RULE_REGISTRATION',
		title: 'Registration',
		body: [
			'Each registered player who has reached the required level is allowed to take part in a tournament.',
			'Each player pays an entry fee to register.',
		],
	},
	{
		key: 'START',
		titleKey: 'T_SITE_TOURNAMENT_RULE_START',
		title: 'Tournament start',
		body: [
			'A tournament starts when 8 participants register.',
			'Participants are notified with a message that the tournament is about to start. If they are playing on a table while waiting for the tournament to start, they can finish the current hand before joining.',
		],
	},
	{
		key: 'BLINDS',
		titleKey: 'T_SITE_TOURNAMENT_RULE_BLINDS',
		title: 'Blinds',
		body: [
			'All players start with the same number of tournament chips. Blinds increase at fixed intervals, so every level puts more pressure on short stacks.',
		],
	},
	{
		key: 'ELIMINATION',
		titleKey: 'T_SITE_TOURNAMENT_RULE_ELIMINATION',
		title: 'Elimination',
		body: [
			'A player is eliminated when they lose all of their tournament chips. The tournament continues until one player holds every chip.',
		],
	},
	{
		key: 'PRIZES',
		titleKey: 'T_SITE_TOURNAMENT_PRIZES',
		title: 'Prizes',
		body: [
			'Prizes are paid to the top 3 finishers and are credited to their balance as soon as the tournament ends.',
		],
		// The design sets the last section's title as a small gold/300 label.
		accent: true,
	},
];

type Props = {
	onClose: () => void;
};

export const TournamentRulesRedesignModal = ({ onClose }: Props) => {
	const { t } = useTranslation();
	const scale = useRedesignScale({ fitHeight: PANEL.height });
	const styles = useMemo(() => createStyles(scale), [scale]);

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			panel={{
				height: PANEL.height,
				radius: PANEL.radius,
				fill: PANEL_FILL,
				rim: PANEL_RIM,
				overScreen: true,
			}}
		>
			<View style={styles.body}>
				<View style={styles.headerGroup}>
					<View style={styles.titleBar}>
						<Text style={styles.title}>
							{t('T_SITE_TOURNAMENT_RULES', { defaultValue: 'Rules' }).toUpperCase()}
						</Text>
						<Pressable
							accessibilityRole='button'
							accessibilityLabel={t('T_SITE_A11Y_CLOSE_RULES', { defaultValue: 'Close rules' })}
							onPress={onClose}
							hitSlop={8}
							style={styles.close}
						>
							<IconCloseSvg width={24 * scale} height={24 * scale} />
						</Pressable>
					</View>
					<Text style={styles.subtitle}>
						{t('T_SITE_TOURNAMENT_RULES_SUBTITLE', { defaultValue: 'Sit & Go Tournaments' })}
					</Text>
				</View>

				<RedesignScrollView
					contentContainerStyle={styles.content}
					indicatorInsets={{ right: 0, top: 0, bottom: 0 }}
				>
					{SECTIONS.map(section => (
						<View key={section.key} style={styles.section}>
							<Text style={[styles.sectionTitle, section.accent && styles.sectionTitleAccent]}>
								{t(section.titleKey, {
									defaultValue: section.title,
								}).toUpperCase()}
							</Text>
							{section.body.map((paragraph, index) => (
								<Text key={index} style={styles.paragraph}>
									{t(`T_SITE_TOURNAMENT_RULES_${section.key}_${index + 1}`, {
										defaultValue: paragraph,
									})}
								</Text>
							))}
						</View>
					))}
				</RedesignScrollView>
			</View>
		</RedesignModal>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	return StyleSheet.create({
		body: {
			flex: 1,
			paddingHorizontal: s(PANEL.paddingX),
			paddingVertical: s(PANEL.paddingY),
			gap: s(PANEL.gap),
		},
		headerGroup: {
			gap: s(2),
		},
		titleBar: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(8),
		},
		title: {
			flex: 1,
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(24),
			lineHeight: s(24 * 1.3),
			color: colors.gold200,
		},
		close: {
			width: s(32),
			height: s(32),
			alignItems: 'center',
			justifyContent: 'center',
		},
		subtitle: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(12),
			lineHeight: s(16),
			color: colors.secondary,
		},
		// 14 on the right keeps the copy clear of the 4-wide track, as the design's "Content" does.
		content: {
			gap: s(16),
			paddingRight: s(14),
		},
		section: {
			gap: s(6),
		},
		// DS/Heading/M in gold/200 (5157:14696).
		sectionTitle: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(18),
			lineHeight: s(18 * 1.3),
			color: colors.gold200,
		},
		// The Prizes section keeps the small DS/Overline/S label (5157:14724).
		sectionTitleAccent: {
			fontWeight: '900',
			fontSize: s(10),
			lineHeight: s(12),
			letterSpacing: s(0.6),
			color: colors.gold300,
		},
		paragraph: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(14),
			lineHeight: s(20),
			color: colors.secondary,
		},
	});
};

export default TournamentRulesRedesignModal;
